import type { BookingSummary } from '@/modules/chat/userDataBridge';
import type { RecoveryCandidate } from './types';

/**
 * Pure helpers for the chat-recovery flow. Kept side-effect-free so the risky
 * decisions (which booking may be cancelled, where the hand-off goes) are
 * unit-testable without a browser.
 */

/**
 * The cancel guard, mirroring yara's `resolveBooking` rule: an id is only
 * honoured if it appears in the list fetched with the user's own session.
 * An invented book_id resolves to null and nothing is cancelled.
 */
export function pickBookingForBook(
  bookings: BookingSummary[],
  bookId: string
): BookingSummary | null {
  if (!bookId) return null;
  return bookings.find(b => b.bookId === String(bookId)) ?? null;
}

/**
 * Hand-off URL for the replacement booking — the same deep link the profile's
 * substitute-doctor button and yara build (`skipTimeSelectStep` included), so the
 * patient lands straight on time-selection for centre 5532.
 */
export function buildHandoffUrl(candidate: Pick<RecoveryCandidate, 'profile_url'>): string | null {
  const profileUrl = String(candidate.profile_url ?? '').trim();
  if (!profileUrl.includes('/dr/')) return null;
  const path = profileUrl.replace('/dr/', '/booking/');
  const joiner = path.includes('?') ? '&' : '?';
  return `${path}${joiner}centerId=5532&skipTimeSelectStep=true`;
}

/**
 * katibe balance lives behind one endpoint whose envelope has shifted before;
 * read it defensively. Returns null when nothing numeric is found — the caller
 * then keeps polling instead of proceeding on a guess.
 */
export function extractBalance(res: unknown): number | null {
  const candidates = [
    (res as any)?.data?.balance,
    (res as any)?.data?.result?.balance,
    (res as any)?.data?.result?.data?.balance,
    (res as any)?.balance,
  ];
  for (const c of candidates) {
    const n = Number(c);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return null;
}

/** Poll until `check` says yes or attempts run out. Testable with a fake clock. */
export async function pollUntil<T>(
  attempt: () => Promise<T | null>,
  { tries, intervalMs }: { tries: number; intervalMs: number }
): Promise<T | null> {
  for (let i = 0; i < tries; i++) {
    try {
      const result = await attempt();
      if (result !== null) return result;
    } catch {
      // A single failed poll is not a failure of the flow.
    }
    if (i < tries - 1) {
      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }
  }
  return null;
}
